/**
 * D-046 (approved 2026-10-11 capacity-aware owner presentation).
 *
 * The owner "also drive" card must reflect the company's authoritative free
 * seats, taken from the licence state the home already loaded (LicenseContext
 * -> getCompanyLicenses) for the selected company. Presenting capacity is a
 * pure presentation concern: this module only decides which card to show and
 * never charges, allocates a seat or mutates a relationship.
 *
 * Critical safety rule: a loading, failed or otherwise unknown licence read
 * must NOT be classified as "no seats". Only a definitive zero free seat
 * count is presented as sold out; anything indeterminate stays `unknown` so
 * the owner is never falsely told the company has no licences.
 */
export type OwnerBecomeDriverSeatState = 'available' | 'full' | 'unknown';

export interface OwnerBecomeDriverSeatInput {
  /** True once the licence source produced authoritative data for the company. */
  hasLicenseInfo: boolean;
  /** The licence request for the selected company is still in flight. */
  isLoading?: boolean;
  /** The licence request for the selected company failed. */
  hasError?: boolean;
  /** Authoritative available company seats, when known. */
  availableSeats?: number | null;
}

/**
 * Tri-state seat availability. `unknown` must never be rendered as "sold out".
 */
export function deriveOwnerBecomeDriverSeatState({
  hasLicenseInfo,
  hasError = false,
  availableSeats,
}: OwnerBecomeDriverSeatInput): OwnerBecomeDriverSeatState {
  // An initial load has no authoritative data. A background revalidation keeps
  // the loaded card stable; the backend still atomically rechecks on submission.
  if (hasError || !hasLicenseInfo) return 'unknown';
  if (typeof availableSeats !== 'number' || Number.isNaN(availableSeats)) return 'unknown';
  return availableSeats > 0 ? 'available' : 'full';
}

interface CompanyLicensesLike {
  summary?: {
    available_licenses?: number;
    capacity?: { available?: number };
  } | null;
}

/**
 * Resolve the authoritative free-seat count from the already-loaded company
 * licence state. The structured capacity is preferred; the legacy summary
 * counter is the fallback. Returns null when neither is available, so callers
 * keep the tri-state (never a false zero).
 */
export function resolveAvailableCompanySeats(
  licenseInfo: CompanyLicensesLike | null | undefined,
): number | null {
  const capacityAvailable = licenseInfo?.summary?.capacity?.available;
  if (typeof capacityAvailable === 'number' && !Number.isNaN(capacityAvailable)) {
    return capacityAvailable;
  }
  const summaryAvailable = licenseInfo?.summary?.available_licenses;
  if (typeof summaryAvailable === 'number' && !Number.isNaN(summaryAvailable)) {
    return summaryAvailable;
  }
  return null;
}
